import React, { useState, useEffect } from 'react';
import { userAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { UserCheck, Check, X, Eye, FileText } from 'lucide-react';

const AdminKYC = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await userAPI.getUsers({ kyc_status: 'pending' });
      setUsers(res.data.filter(u => u.kyc_document));
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await userAPI.updateKYCStatus(userId, 'approved');
      toast.success('KYC approved');
      loadUsers();
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to approve KYC');
    }
  };

  const handleReject = async (userId) => {
    try {
      await userAPI.updateKYCStatus(userId, 'rejected');
      toast.success('KYC rejected');
      loadUsers();
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to reject KYC');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-700 border-green-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return (
      <Badge variant="outline" className={styles[status] || 'bg-gray-100 text-gray-700'}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-kyc">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <UserCheck className="h-6 w-6 text-[#4E50FF]" />
          KYC Approvals
        </h1>
        <p className="text-slate-500 mt-1">Review and approve user verifications</p>
      </div>

      {users.length > 0 ? (
        <Card className="border shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-slate-600">User</TableHead>
                <TableHead className="text-slate-600">Role</TableHead>
                <TableHead className="text-slate-600">IC Number</TableHead>
                <TableHead className="text-slate-600">Status</TableHead>
                <TableHead className="text-slate-600">Submitted</TableHead>
                <TableHead className="text-slate-600">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} data-testid={`kyc-row-${user.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#4E50FF]/10 flex items-center justify-center">
                        <span className="text-[#4E50FF] font-semibold text-sm">
                          {user.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{user.full_name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-700 capitalize">{user.role}</TableCell>
                  <TableCell className="text-slate-700 font-mono">{user.ic_number || '-'}</TableCell>
                  <TableCell>{getStatusBadge(user.kyc_status)}</TableCell>
                  <TableCell className="text-slate-500">{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedUser(user)}
                        data-testid={`view-kyc-${user.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(user.id)}
                        className="bg-green-500 hover:bg-green-600 text-white"
                        data-testid={`approve-kyc-${user.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(user.id)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`reject-kyc-${user.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="text-center py-12 border shadow-sm">
          <CardContent>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Pending KYC</h3>
            <p className="text-slate-500">All KYC requests have been processed</p>
          </CardContent>
        </Card>
      )}

      {/* KYC Document Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#4E50FF]" />
              KYC Document Review
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Full Name</p>
                  <p className="text-slate-900 font-medium">{selectedUser.full_name}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">IC Number</p>
                  <p className="text-slate-900 font-mono">{selectedUser.ic_number}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Bank Name</p>
                  <p className="text-slate-900">{selectedUser.bank_name || '-'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Bank Account</p>
                  <p className="text-slate-900 font-mono">{selectedUser.bank_account || '-'}</p>
                </div>
              </div>

              {selectedUser.kyc_document && (
                <div className="border rounded-lg overflow-hidden">
                  <p className="text-sm text-slate-500 p-3 bg-slate-50 border-b">KYC Document</p>
                  <div className="p-4 bg-white">
                    <img
                      src={selectedUser.kyc_document}
                      alt="KYC Document"
                      className="max-w-full h-auto rounded-lg"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleReject(selectedUser.id)}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleApprove(selectedUser.id)}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminKYC;
